import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, GitBranch, Loader2 } from "lucide-react";
import { api, inr, shortDate } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const TONE = {
  Paid: "default", Dispatched: "default", Delivered: "default", Generated: "default",
  Approved: "default", Converted: "default", Cancelled: "destructive", Failed: "destructive",
  Expired: "destructive", Unpaid: "outline", Draft: "outline", Pending: "outline",
};

/** Compact, clickable chain of every document around a record. Read-only. */
export function DocumentJourney({ entity, id, compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(!compact);

  useEffect(() => {
    if (!id) return;
    api.get(`/journey/${entity}/${id}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ stages: [] }));
  }, [entity, id]);

  if (!data) {
    return (
      <div className="flex h-12 items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading journey…
      </div>
    );
  }
  const stages = (data.stages || []).filter((s) => s.count > 0 || s.kind !== "eway");
  if (stages.length === 0) return null;

  return (
    <div className="surface p-3 sm:p-4" data-testid={`document-journey-${entity}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Document journey</span>
          {data.customer_name && (
            <span className="text-xs text-muted-foreground">{data.customer_name}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data.totals && (
            <span data-testid="journey-totals">
              Billed {inr(data.totals.billed)} • Received {inr(data.totals.received)} •
              Outstanding {inr(data.totals.outstanding)}
            </span>
          )}
          {compact && (
            <Button size="sm" variant="ghost" data-testid="journey-toggle"
              onClick={() => setOpen((v) => !v)}>
              {open ? "Hide" : "Show"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {stages.map((s, i) => (
          <div key={s.kind} className="flex items-center gap-1.5">
            <span
              data-testid={`journey-stage-${s.kind}`}
              className={`rounded-full border px-2.5 py-1 text-[11px] ${
                s.done ? "border-primary/40 bg-primary/10 font-medium" : "border-dashed text-muted-foreground"
              }`}
            >
              {s.label}{s.count > 1 ? ` ×${s.count}` : ""} {s.done ? "✓" : "○"}
            </span>
            {i < stages.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
          </div>
        ))}
      </div>

      {open && (
        <div className="mt-3 grid gap-1.5">
          {stages.filter((s) => s.done).map((s) => (
            <div key={s.kind} className="flex flex-wrap items-center gap-1.5">
              <span className="w-36 shrink-0 text-xs text-muted-foreground">{s.label}</span>
              {s.documents.map((d) => (
                <Link key={`${d.kind}-${d.id}-${d.number}`} to={d.route}
                  data-testid={`journey-doc-${d.kind}-${d.id}`}
                  className={`flex items-center gap-1.5 rounded border px-2 py-1 text-[11px] transition-colors hover:bg-accent ${
                    d.is_focus ? "border-primary" : "border-border"
                  }`}>
                  <span className="font-mono-num">{d.number || "—"}</span>
                  <Badge variant={TONE[d.status] || "secondary"} className="text-[9px]">
                    {d.status}
                  </Badge>
                  {d.amount > 0 && <span className="text-muted-foreground">{inr(d.amount)}</span>}
                  {d.valid_till && (
                    <span className="text-muted-foreground">till {shortDate(d.valid_till)}</span>
                  )}
                  {d.mock && (
                    <Badge variant="destructive" className="text-[9px]"
                      data-testid={`journey-eway-mock-${d.id}`}>SANDBOX</Badge>
                  )}
                  {d.date && <span className="text-muted-foreground">{shortDate(d.date)}</span>}
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
